<template>
  <v-container fluid class="view-page pa-0">
    <v-card outlined class="view-card" :loading="loading">
      <div class="view-header">
        <div class="title-wrap">
          <v-btn icon small class="mr-2" @click="$router.go(-1)"><v-icon>mdi-chevron-left</v-icon></v-btn>
          <div>
            <div class="page-title">{{ delivery ? delivery.referenceNumber : "Inbound Delivery" }}</div>
            <div v-if="delivery" class="page-subtitle">Received {{ delivery.createdAt | moment("MM/DD/YYYY [at] LT") }}</div>
          </div>
        </div>
        <div v-if="delivery && delivery.status !== 'converted'" class="header-actions">
          <v-btn rounded outlined color="error" @click="remove">
            <v-icon left>mdi-delete-outline</v-icon>Delete
          </v-btn>
          <v-btn rounded color="primary" dark @click="convert">
            <v-icon left>mdi-import</v-icon>Import into Purchase
          </v-btn>
        </div>
        <v-btn v-else-if="delivery && delivery.Purchase" rounded outlined color="primary" @click="openPurchase">
          Open Purchase #{{ delivery.Purchase.localId }}
        </v-btn>
      </div>

      <v-card-text v-if="delivery" class="pa-4">
        <div class="summary-grid">
          <div class="summary-box"><div class="summary-label">Received By</div><div class="summary-text">{{ delivery.DeliveryPerson ? delivery.DeliveryPerson.name : "Unknown" }}</div></div>
          <div class="summary-box"><div class="summary-label">Entries</div><div class="summary-value">{{ delivery.Details.length }}</div></div>
          <div class="summary-box"><div class="summary-label">Total Boxes</div><div class="summary-value">{{ totalBoxes }}</div></div>
          <div class="summary-box"><div class="summary-label">Status</div><div class="summary-text text-capitalize">{{ delivery.status }}</div></div>
        </div>

        <v-alert v-if="delivery.note" text type="info" class="mt-4 mb-0">{{ delivery.note }}</v-alert>

        <div class="section-title mt-5 mb-3">Received Products</div>
        <v-data-table :headers="headers" :items="delivery.Details" hide-default-footer
          :items-per-page="500" :mobile-breakpoint="0" class="detail-table">
          <template v-slot:item="{ item, index }">
            <tr>
              <td>{{ index + 1 }}</td>
              <td><div class="font-weight-bold">{{ item.productName }}</div><div class="page-subtitle">{{ item.scannedCode }} · {{ item.scanType.toUpperCase() }}</div></td>
              <td>{{ item.Product ? item.Product.sku || "--" : "--" }}</td>
              <td class="text-center font-weight-bold">{{ item.quantity }}</td>
              <td class="text-center">{{ item.unitCountInBox || "--" }}</td>
            </tr>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import service from "../service";
import { mapActions, mapGetters } from "vuex";
import Hashids from "hashids";
const hashids = new Hashids();
export default {
  name: "InboundDeliveryView",
  data: () => ({ loading: false, delivery: null, headers: [
    { text: "#", sortable: false }, { text: "Product", value: "productName" },
    { text: "SKU", value: "Product.sku" }, { text: "Boxes", value: "quantity", align: "center" },
    { text: "Units / Box", value: "unitCountInBox", align: "center" },
  ] }),
  computed: {
    ...mapGetters("purchase", ["PurchaseDetails"]),
    totalBoxes() { return this.delivery.Details.reduce((sum, row) => sum + Number(row.quantity || 0), 0); },
  },
  created() { this.load(); },
  methods: {
    ...mapActions("purchase", ["ResetPurchase"]),
    async load() {
      this.loading = true;
      try { const response = await service.getById(this.$route.params.id); this.delivery = response.data; }
      catch (error) { this.$swal("Inbound Delivery", error.data && error.data.message ? error.data.message : "Unable to load delivery.", "error"); }
      finally { this.loading = false; }
    },
    async convert() {
      if (this.PurchaseDetails.length) {
        const result = await this.$swal({ title: "Replace running purchase?", text: "Importing this delivery will replace the current unsaved purchase.", icon: "warning", showCancelButton: true, confirmButtonText: "Continue" });
        if (!result.value) return;
      }
      this.ResetPurchase();
      this.$router.push(`/inventory/purchase/create?InboundDeliveryId=${this.delivery.id}`);
    },
    async remove() {
      const result = await this.$swal({
        title: "Delete inbound delivery?",
        text: `${this.delivery.referenceNumber || "This delivery"} will be removed. This is a soft delete and remains recoverable from the database.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d32f2f",
        confirmButtonText: "Delete Delivery",
      });
      if (!result.value) return;

      this.loading = true;
      try {
        await service.remove(this.delivery.id);
        await this.$swal.mixin({ toast: true, position: "top-end", showConfirmButton: false, timer: 2000 }).fire({
          icon: "success",
          title: "Inbound delivery deleted",
        });
        this.$router.push("/inventory/inboundDelivery/list");
      } catch (error) {
        this.$swal("Inbound Delivery", error.data && error.data.message ? error.data.message : "Unable to delete delivery.", "error");
      } finally {
        this.loading = false;
      }
    },
    openPurchase() {
      const hashId = hashids.encode(this.delivery.Purchase.id);
      const routeData = this.$router.resolve({
        path: `/global/purchase/${hashId}/view`,
      });
      window.open(routeData.href, "newwindow", "width=850, height=1100");
    },
  },
};
</script>

<style scoped>
.view-page{background:#f8fafc}.view-card{border-radius:20px;background:#fff}.view-header{padding:16px;border-bottom:1px solid #e2e8f0;display:flex;align-items:center;justify-content:space-between;gap:16px;flex-wrap:wrap}.title-wrap,.header-actions{display:flex;align-items:center}.header-actions{gap:10px}.page-title{font-size:22px;font-weight:800;color:#0f172a}.page-subtitle{font-size:12px;color:#64748b;margin-top:2px}.summary-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:12px}.summary-box{background:#f8fafc;border-radius:16px;padding:14px}.summary-label{font-size:12px;color:#64748b}.summary-value{font-size:22px;font-weight:800;color:#0f172a;margin-top:4px}.summary-text{font-size:15px;font-weight:800;color:#0f172a;margin-top:5px}.section-title{font-size:17px;font-weight:800;color:#0f172a}.detail-table{border:1px solid #e2e8f0;border-radius:16px;overflow:hidden}@media(max-width:700px){.summary-grid{grid-template-columns:1fr 1fr}.header-actions{width:100%}.header-actions .v-btn{flex:1}}
</style>
